/*
 * <shipping-estimator> — PDP delivery estimate (snippets/pdp-shipping-rj.liquid).
 *
 * The markup already works without this file: tabs, showrooms and all copy are
 * server-rendered. This element adds:
 *   - the postcode lookup (AddressFinder suburb search → DataJet rates),
 *   - rendering rates into [data-rates] only (errors never touch the tabs or
 *     the Showrooms panel), with stale responses ignored,
 *   - re-quoting when the PDP quantity changes,
 *   - a short screen-reader status instead of re-announcing the whole block.
 *
 * No layout is ever read (no offsetHeight / getBoundingClientRect) and there are
 * no observers, so it never forces a reflow. Every height change is a CSS grid-row
 * reveal: this script only flips data-open (which state box shows in the
 * Shipping panel); row open/close and More/Less info are CSS. Tab switching is
 * the shared assets/pdp-tabs.js.
 */
(() => {
  if (customElements.get('shipping-estimator')) return;

  const COOKIE = 'customer_postcode';
  const RATE_ORDER = { standard: 0, premium: 1, vip: 2 };
  let addressFinderScript; // shared promise: the third-party script loads once per page

  function loadAddressFinder() {
    if (window.AddressFinder) return Promise.resolve();
    if (!addressFinderScript) {
      addressFinderScript = new Promise((resolve, reject) => {
        const script = document.createElement('script');
        script.src = 'https://api.addressfinder.io/assets/v3/widget.js';
        script.async = true;
        script.onload = resolve;
        script.onerror = reject;
        document.head.appendChild(script);
      });
    }
    return addressFinderScript;
  }

  // Same "postcode|state|suburb|full location" cookie the cart page's calculator reads.
  function readSavedLocation() {
    const match = document.cookie.match(new RegExp('(?:^|;\\s*)' + COOKIE + '=([^;]+)'));
    if (!match) return null;
    const [postcode, state, locality, fullLocation] = match[1].split('|');
    return postcode && fullLocation ? { postcode, state, locality, fullLocation } : null;
  }

  function saveLocation(location) {
    const value = [location.postcode, location.state, location.locality, location.fullLocation].join('|');
    document.cookie = `${COOKIE}=${value};path=/;SameSite=Lax`;
  }

  function rateKind(code) {
    const value = String(code || '').toLowerCase();
    if (value.includes('vip')) return 'vip';
    if (value.includes('two_man') || value.includes('premium')) return 'premium';
    return 'standard';
  }

  function formatMoney(amount) {
    return '$' + (Number.isInteger(amount) ? String(amount) : amount.toFixed(2));
  }

  class ShippingEstimator extends HTMLElement {
    connectedCallback() {
      this.input = this.querySelector('input[type="text"]');
      this.ratesArea = this.querySelector('[data-rates]');
      this.status = this.querySelector('[data-shipping-status]');
      this.item = JSON.parse(this.querySelector('[data-shipping-item]').textContent);
      this.quantityInput = this.dataset.quantityInput ? document.getElementById(this.dataset.quantityInput) : null;
      this.requestId = 0;
      this.hasRates = false;
      this.location = null;

      this.addEventListener('click', this);
      this.input.addEventListener('focus', this, { once: true });
      if (this.quantityInput) this.quantityInput.addEventListener('change', this);

      const saved = readSavedLocation();
      if (saved) {
        this.input.value = saved.fullLocation;
        this.lookup(saved);
      }
    }

    disconnectedCallback() {
      if (this.controller) this.controller.abort();
      clearTimeout(this.quantityTimer);
      if (this.quantityInput) this.quantityInput.removeEventListener('change', this);
    }

    handleEvent(event) {
      if (event.type === 'focus') return this.initAddressFinder();
      if (event.type === 'change') return this.onQuantityChange();

      if (event.target.closest('[data-action="retry"]') && this.location) this.lookup(this.location);
    }

    // AddressFinder is only needed once the shopper starts typing, so it loads on first focus.
    initAddressFinder() {
      loadAddressFinder()
        .then(() => {
          if (!this.isConnected || this.widget) return;
          this.widget = new window.AddressFinder.Widget(this.input, this.dataset.addressfinderKey, 'AU', {
            show_locations: true,
            show_addresses: false,
            location_params: { location_types: 'locality' },
            // The suggestion list renders inside our input wrapper, so CSS sizes it (no JS measuring).
            container: this.querySelector('[data-af-container]'),
          });
          this.widget.on('location:select', (fullLocation, meta) => {
            const location = {
              postcode: meta.postcode,
              state: meta.state_territory,
              locality: meta.locality_name,
              fullLocation: meta.full_location,
            };
            saveLocation(location);
            this.lookup(location);
          });
        })
        .catch(() => {
          // Suburb search unavailable; showrooms and any saved estimate still work.
        });
    }

    onQuantityChange() {
      if (!this.location) return;
      clearTimeout(this.quantityTimer);
      this.quantityTimer = setTimeout(() => this.lookup(this.location), 400);
    }

    quantity() {
      const value = this.quantityInput ? parseInt(this.quantityInput.value, 10) : 1;
      return value > 0 ? value : 1;
    }

    lookup(location) {
      this.location = location;
      const requestId = ++this.requestId;
      if (this.controller) this.controller.abort();
      this.controller = new AbortController();

      if (this.hasRates) this.fitShimmer();
      this.showView(this.hasRates ? 'updating' : 'loading');
      this.announce('Loading delivery options…');

      const body = {
        rate: {
          destination: {
            country: 'AU',
            postal_code: location.postcode,
            province: location.state,
            city: location.locality,
          },
          items: [{ ...this.item, quantity: this.quantity() }],
          currency: 'AUD',
          locale: 'en-AU',
          page: 'product',
        },
      };

      fetch(this.dataset.endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(body),
        signal: this.controller.signal,
      })
        .then((response) => {
          if (!response.ok) throw new Error(`Shipping rates request failed: ${response.status}`);
          return response.json();
        })
        .then((data) => {
          if (requestId !== this.requestId) return; // a newer lookup has started
          this.renderRates(Array.isArray(data.rates) ? data.rates : []);
        })
        .catch((error) => {
          if (error.name === 'AbortError' || requestId !== this.requestId) return;
          // Pickup doesn't depend on DataJet: still offer it, with the error underneath.
          if (this.pickupAvailable()) {
            this.showOptions([], 'error');
            this.announce(`Couldn't load delivery prices. Warehouse pickup is available for ${location.fullLocation}.`);
            return;
          }
          this.hasRates = false;
          this.showView('error');
          this.announce("Couldn't load delivery prices.");
        });
    }

    renderRates(rawRates) {
      const hiddenCode = this.hasAttribute('data-b2b') ? 'retail' : 'trade';
      const rates = rawRates
        .filter((rate) => !String(rate.service_code || '').includes(hiddenCode))
        .map((rate) => ({
          // DataJet service names can end in ":" ("Standard Delivery:") — not shown in the design.
          name: String(rate.service_name || '').replace(/\s*:\s*$/, ''),
          price: parseInt(rate.total_price, 10) / 100,
          kind: rateKind(rate.service_code),
        }))
        .filter((rate) => rate.name && !Number.isNaN(rate.price))
        .sort((a, b) => (RATE_ORDER[a.kind] ?? 9) - (RATE_ORDER[b.kind] ?? 9));

      const where = this.querySelector('[data-view="empty"] [data-location]');
      if (where) where.textContent = this.location.fullLocation;

      if (!rates.length) {
        if (this.pickupAvailable()) {
          this.showOptions([], 'empty');
          this.announce(`No delivery options found for ${this.location.fullLocation}. Warehouse pickup is available.`);
          return;
        }
        this.hasRates = false;
        this.showView('empty');
        this.announce(`No delivery options found for ${this.location.fullLocation}.`);
        return;
      }

      this.showOptions(rates);
      const count = this.ratesArea.querySelectorAll('.pdp-ship-option').length; // incl. pickup
      this.announce(`${count} delivery option${count === 1 ? '' : 's'} for ${this.location.fullLocation}.`);
    }

    // Writes the option rows into [data-rates] and reveals them: Warehouse Pickup
    // first (when the postcode qualifies), then the delivery rates. `notice`
    // ('empty' | 'error') appends that state's message under the rows, for when
    // pickup is the only option shown.
    showOptions(rates, notice) {
      const rateTemplate = this.querySelector('template[data-template="rate"]');
      const fragment = document.createDocumentFragment();

      rates.forEach((rate) => {
        const row = rateTemplate.content.cloneNode(true);
        row.querySelector('[data-rate-name]').textContent = rate.name;
        row.querySelector('[data-rate-price]').textContent = formatMoney(rate.price);
        const inclusions = this.querySelector(`template[data-inclusions="${rate.kind}"]`);
        if (inclusions) row.querySelector('[data-rate-inclusions]').append(inclusions.content.cloneNode(true));
        fragment.append(row);
      });
      if (this.pickupAvailable()) {
        fragment.prepend(this.querySelector('template[data-template="pickup"]').content.cloneNode(true));
      }
      if (notice) {
        const message = this.querySelector(`[data-view="${notice}"] .pdp-shipping__state-inner`);
        if (message) fragment.append(...Array.from(message.childNodes, (node) => node.cloneNode(true)));
      }

      // The rates box is closed at this point (loading/updating is showing), so
      // its content is swapped out of view and then revealed.
      this.ratesArea.replaceChildren(fragment);
      this.optionCount = this.ratesArea.querySelectorAll('.pdp-ship-option').length; // incl. pickup
      this.showView('rates');
      this.hasRates = true;
      this.openNearestShowroom();
    }

    // The "updating" shimmer shows one row per option currently shown (incl.
    // Warehouse Pickup), so swapping rows for shimmer doesn't change the count.
    fitShimmer() {
      const shimmer = this.querySelector('.pdp-shipping__shimmer');
      if (!shimmer || !shimmer.firstElementChild) return;
      const want = Math.max(1, this.optionCount || shimmer.children.length);
      while (shimmer.children.length < want) shimmer.append(shimmer.firstElementChild.cloneNode(true));
      while (shimmer.children.length > want) shimmer.lastElementChild.remove();
    }

    // Warehouse Pickup is offered for postcodes in data-pickup-postcodes ("3000-3207").
    pickupAvailable() {
      const [min, max] = String(this.dataset.pickupPostcodes || '').split('-').map(Number);
      const postcode = parseInt(this.location.postcode, 10);
      return postcode >= min && postcode <= max;
    }

    // Open exactly one state box in the Shipping panel; CSS animates the
    // outgoing box closed and the incoming one open at the same time.
    showView(name) {
      this.querySelectorAll('.pdp-shipping__state').forEach((box) => {
        box.toggleAttribute('data-open', box.dataset.view === name);
      });
    }

    announce(message) {
      if (this.status) this.status.textContent = message;
    }

    // Pre-opens the shopper's nearest showroom row (does not switch tabs).
    openNearestShowroom() {
      const nearest = { SA: 'VIC', WA: 'VIC', TAS: 'VIC', NT: 'QLD', ACT: 'NSW' };
      const state = nearest[this.location.state] || this.location.state;
      const row = this.querySelector(`.pdp-showroom-item[data-state="${state}"]`);
      if (row) row.open = true;
    }
  }

  customElements.define('shipping-estimator', ShippingEstimator);
})();
